import { ComponentChildren, JSX } from 'preact';

import { classNames } from '#utils/UNSAFE_classNames';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { Size } from '#utils/UNSAFE_size';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { RadioRedwoodTheme } from '#UNSAFE_RadioItem/themes/redwood/RadioTheme';
import { LabelRedwoodTheme } from '#UNSAFE_Label/themes/redwood/LabelTheme';
import { FormLayoutRedwoodTheme } from '#UNSAFE_TextField/themes/redwood/FormLayoutTheme';
import { CheckboxRedwoodTheme } from '#UNSAFE_Checkbox/themes/redwood/CheckboxTheme';
import {
  labelSlotStyles,
  labelInnerStyles
} from '#UNSAFE_LabelValueLayout/LabelValueLayoutStyles.css';
import { StyledLabelValueLayout } from '#UNSAFE_LabelValueLayout/StyledLabelValueLayout';
import { checkboxRadioLabelInnerStyles } from './CheckboxRadioLabelValueLayoutStyles.css';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { computeStartLabelWidth } from '#utils/UNSAFE_formControlUtils';

type Props = {
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * Defines how the label is going to be positioned. For CheckboxSet or RadioSet the start, top and inside values are supported.
   */
  labelEdge?: 'start' | 'top' | 'inside';
  /**
   * Specifies if the parent component is a CheckboxSet or RadioSet
   */
  parentComponentVariant: 'radioSet' | 'checkboxSet';
  /**
   * Specifies if there is assistive text or links at the top of the component below the label
   */
  hasTopUserAssistance?: boolean;
  /**
   * Label content of the layout
   */
  label?: ComponentChildren;
  /**
   * Defines the label width for labelEdge 'start' ('top' is always 100%)
   */
  labelStartWidth?: Size;
  /**
   * Value content of the layout
   */
  children: ComponentChildren;
};

/**
 * A layout component for rendering a label and value pair, used in form components
 * such as CheckboxSet and RadioSet.
 */
export const CheckboxRadioLabelValueLayout = ({
  columnSpan = 1,
  label,
  labelEdge,
  children,
  parentComponentVariant,
  labelStartWidth = '33%',
  hasTopUserAssistance = false
}: Props) => {
  const { baseTheme: radioTheme } = useComponentTheme(RadioRedwoodTheme);
  const { baseTheme: checkboxTheme } = useComponentTheme(CheckboxRedwoodTheme);
  const { baseTheme: labelTheme } = useComponentTheme(LabelRedwoodTheme);
  const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);

  const isRadio = parentComponentVariant === 'radioSet';
  const isCheckbox = parentComponentVariant === 'checkboxSet';
  const isStart = labelEdge === 'start';
  const isTop = labelEdge === 'top' || labelEdge === 'inside'; // 'inside' needs to pick up all 'top' styles.
  const isInside = labelEdge === 'inside';

  // JET-68190 - labelStartWidth needs to take columnSpan into account
  const width = computeStartLabelWidth(labelStartWidth, columnSpan);
  const valueWidth = `calc(100% - ${width})`;

  const labelStyles: JSX.CSSProperties = isStart
    ? { flexBasis: width, width: width, maxWidth: width }
    : {};

  const valueStyles: JSX.CSSProperties = isStart
    ? { flexBasis: valueWidth, width: valueWidth, maxWidth: valueWidth }
    : {};

  const { isFormLayout, isReadonly: isFormReadonly, labelWrapping } = useFormContext();
  const { isReadonly } = useFormFieldContext();
  const isPureReadonly = (isFormLayout && isFormReadonly) || (!isFormLayout && isReadonly);

  const labelStyleClasses = classNames([
    formLayoutTheme,
    labelSlotStyles.base,
    isStart && labelSlotStyles.start,
    isTop && labelSlotStyles.top,
    isTop && hasTopUserAssistance && labelSlotStyles.topAndTopUA,
    isTop && isPureReadonly && labelSlotStyles.topPureReadonly
  ]);

  const labelInnerClasses = classNames([
    isRadio && radioTheme,
    isCheckbox && checkboxTheme,
    labelTheme,
    formLayoutTheme,
    labelInnerStyles.base,
    isStart && labelInnerStyles.start,
    isRadio && isInside && checkboxRadioLabelInnerStyles.insideRadioSet,
    isCheckbox && isInside && checkboxRadioLabelInnerStyles.insideCheckboxSet,
    isRadio &&
      isStart &&
      !hasTopUserAssistance &&
      checkboxRadioLabelInnerStyles.startRadioSetWithoutTopUA,
    isCheckbox &&
      isStart &&
      !hasTopUserAssistance &&
      checkboxRadioLabelInnerStyles.startCheckboxSetWithoutTopUA,
    labelWrapping === 'truncate' && labelInnerStyles.noWrap
  ]);

  return (
    <StyledLabelValueLayout
      // For readonly start aligned labels, we want the div that is the parent of the label to
      // stretch to the height of the value content, including the end content (InputSensitiveText)
      flexAlign={isReadonly && isStart ? 'stretch' : 'start'}
      labelStyleClasses={labelStyleClasses}
      labelStyles={labelStyles}
      labelInnerClasses={labelInnerClasses}
      label={label}
      valueStyles={valueStyles}>
      {children}
    </StyledLabelValueLayout>
  );
};
